import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

/**
 * Detects if the user is using Brave browser
 */
const isBraveBrowser = () => {
  return (navigator.brave && navigator.brave.isBrave) || false;
};

/**
 * Waits for service worker to be ready before proceeding
 */
const waitForServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported in this browser');
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    console.log('Service worker is ready:', registration);
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    throw error;
  }
};

/**
 * Retries a function with exponential backoff
 */
const retryWithBackoff = async (fn, maxRetries = 3, initialDelay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;

      const delay = initialDelay * Math.pow(2, i);
      console.log(`Retry attempt ${i + 1}/${maxRetries} after ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Firebase project configuration object
// This contains all the details required to connect to the Firebase project
const firebaseConfig = {
  apiKey: "AIzaSyDcEkj36_lwk9iOe8Svvqi0ZZMEc_kJ--A",
  authDomain: "mbm-notification.firebaseapp.com",
  projectId: "mbm-notification",
  storageBucket: "mbm-notification.firebasestorage.app",
  messagingSenderId: "35234942742",
  appId: "1:35234942742:web:f31b5091217348eb5fca37",
  measurementId: "G-483SXDMRFV",
};

// Initialize the Firebase app with the provided configuration
const firebaseApp = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging (FCM) to handle push notifications
// Note: getMessaging() may fail on browsers that don't support it (e.g. iOS Safari)
let messaging = null;
try {
  messaging = getMessaging(firebaseApp);
} catch (error) {
  console.warn("Firebase messaging not supported in this browser:", error.message);
}

// VAPID (Voluntary Application Server Identification for Web Push)
// This is a public key used for authentication when sending push notifications from Firebase to the browser.
let vapID =
  "BJgqSdY-7gYS7wbPK4iTesSopAGl7SBXwbZzp4UfbzmKvB7CudVdJaifpdeb7ewMAIhoOM2KNoB0hy2fjel8Vv8";

/**
 * Requests permission from the user to receive notifications and retrieves the FCM token.
 * Use Case:
 * - This function is used when a user visits the app and allows notifications.
 * - The generated token is required to send push notifications to the specific device.
 */
export const requestFCMToken = async (setTokenCallback) => {
  try {
    // Check if user is on Brave browser
    const isBrave = await isBraveBrowser();
    if (isBrave) {
      console.warn("⚠️ Brave browser detected. Push notifications may have limited support.");
      console.log("💡 Tip: If notifications don't work, try:");
      console.log("   1. Disable Brave Shields for this site");
      console.log("   2. Check brave://settings/privacy for notification settings");
    }

    // Check if notification permission is already granted
    let permission = Notification.permission;

    // Only request permission if it hasn't been decided yet
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }

    if (permission === "granted") {
      // Wait for service worker to be ready before attempting to get token
      await waitForServiceWorker();

      const vapidKey = vapID; // Use the predefined VAPID key for authentication

      // Use retry logic to handle transient failures
      const currentToken = await retryWithBackoff(async () => {
        const token = await getToken(messaging, { vapidKey });
        if (!token) {
          throw new Error("No FCM token received from Firebase");
        }
        return token;
      }, 3, 1000);

      console.log("✅ FCM token retrieved successfully");
      setTokenCallback(currentToken); // Store the token if received

    } else if (permission === "denied") {
      console.warn("❌ Notification permission was denied. Please enable it in browser settings.");
      if (isBrave) {
        console.log("📌 For Brave: Go to brave://settings/content/notifications");
      }
    }
  } catch (error) {
    console.error("❌ Error getting FCM token:", error);

    // Provide browser-specific troubleshooting
    const isBrave = await isBraveBrowser();
    if (isBrave && error.code === "messaging/registration-failed") {
      console.error("🔧 Brave-specific error detected:");
      console.error("   → This is likely due to Brave's privacy features blocking Google's push service");
      console.error("   → Try disabling Brave Shields for this site");
      console.error("   → Alternative: Use Chrome, Firefox, or Edge for full notification support");
    } else if (error.name === "AbortError") {
      console.error("🔧 Push service registration failed:");
      console.error("   → Your browser may not support Firebase Cloud Messaging");
      console.error("   → Try a different browser or check your browser settings");
    } else {
      console.error("🔧 Troubleshooting steps:");
      console.error("   1. Check if service worker is registered");
      console.error("   2. Verify internet connection");
      console.error("   3. Clear browser cache and try again");
      console.error("   4. Check browser console for additional errors");
    }
  }
};

/**
 * Singleton pattern to ensure only ONE message listener is registered globally
 */
let messageListenerRegistered = false;
let messageListenerUnsubscribe = null;

/**
 * Registers a SINGLE global message listener for foreground notifications
 * Uses singleton pattern to prevent duplicate listeners
 */
export const registerMessageListener = (callback) => {
  if (!messaging) {
    console.warn("[Firebase] Messaging not supported, skipping listener registration");
    return null;
  }

  if (messageListenerRegistered) {
    console.log("[Firebase] Message listener already registered, skipping duplicate");
    return messageListenerUnsubscribe;
  }

  console.log("[Firebase] Registering global message listener");
  messageListenerRegistered = true;

  messageListenerUnsubscribe = onMessage(messaging, (payload) => {
    // Message received - pass to callback
    if (callback) {
      callback(payload);
    }
  });

  return messageListenerUnsubscribe;
};

/**
 * Unregisters the global message listener
 */
export const unregisterMessageListener = () => {
  if (messageListenerUnsubscribe) {
    console.log("[Firebase] Unregistering global message listener");
    messageListenerUnsubscribe();
    messageListenerUnsubscribe = null;
    messageListenerRegistered = false;
  }
};

/**
 * DEPRECATED: Use registerMessageListener instead
 * Listens for push notifications when the app is in the foreground.
 */
export const onMessageListener = () =>
  new Promise((resolve, reject) => {
    if (!messaging) {
      reject(new Error("Firebase messaging not supported in this browser"));
      return;
    }
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });

export { firebaseApp, messaging };
